import React, { useState, useEffect, useRef, useCallback } from 'react';
import API from '../api';

/**
 * 1:1 direct-message chat component.
 * Props:
 *   otherUserId   — id of the user you're DM'ing
 *   me            — current user object { id, username, is_admin }
 *   onBlockChange — optional callback when block state toggles
 *   height        — optional scroller height (default 240)
 *   title         — override the header label
 *   emptyText     — override what an empty thread says
 *   allowBlock    — show the Block button (default true)
 *
 * The last three exist for the support thread. It is the same transport and the
 * same component, but "Start the trash talk 🔥" is a poor thing to say to
 * somebody who opened this because the app is broken, and offering to block the
 * only person who can help them is worse. The backend refuses that block too;
 * this just keeps the button from being there to press.
 */

const s = {
  wrap: {
    background: 'rgba(0,0,0,0.35)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 12,
    padding: 12,
    display: 'flex',
    flexDirection: 'column',
    gap: 8
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  title: {
    fontFamily: "'Oswald', sans-serif",
    fontSize: 13,
    fontWeight: 700,
    color: '#7fb3ff',
    letterSpacing: 1
  },
  scroller: {
    overflowY: 'auto',
    background: 'rgba(0,0,0,0.25)',
    border: '1px solid rgba(255,255,255,0.05)',
    borderRadius: 8,
    padding: '8px 10px',
    display: 'flex',
    flexDirection: 'column',
    gap: 6
  },
  msgRow: {
    display: 'flex',
    gap: 8,
    alignItems: 'flex-start',
    padding: '4px 0',
    position: 'relative'
  },
  author: { fontWeight: 700, fontSize: 12, color: '#7fb3ff', flexShrink: 0 },
  authorMine: { color: '#ffb347' },
  body: { fontSize: 13, color: '#dde', wordBreak: 'break-word', whiteSpace: 'pre-wrap', flex: 1 },
  time: { fontSize: 10, color: '#556', marginLeft: 4 },
  delBtn: {
    background: 'none', border: 'none', color: '#f44336',
    fontSize: 13, cursor: 'pointer', opacity: 0.5, padding: '0 4px', lineHeight: 1
  },
  empty: { textAlign: 'center', color: '#445', fontSize: 13, padding: '24px 10px' },
  inputRow: { display: 'flex', gap: 8 },
  input: {
    flex: 1, padding: '9px 12px', background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.12)', borderRadius: 7,
    color: '#fff', fontSize: 14, outline: 'none'
  },
  sendBtn: {
    padding: '9px 16px', borderRadius: 7, border: 'none',
    background: 'linear-gradient(135deg, #D50A0A, #a00808)',
    color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer'
  },
  notice: {
    fontSize: 12, color: '#ffb347', padding: '8px 10px',
    background: 'rgba(255,179,71,0.08)',
    border: '1px solid rgba(255,179,71,0.2)', borderRadius: 8
  },
  noticeErr: {
    fontSize: 12, color: '#ff8888', padding: '8px 10px',
    background: 'rgba(213,10,10,0.08)',
    border: '1px solid rgba(213,10,10,0.2)', borderRadius: 8
  },
  blockBtn: {
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.12)',
    color: '#aab', fontSize: 11, padding: '4px 10px',
    borderRadius: 14, cursor: 'pointer', fontWeight: 600
  },
  blockBtnActive: {
    background: 'rgba(213,10,10,0.15)',
    border: '1px solid rgba(213,10,10,0.35)',
    color: '#ff8888'
  }
};

function formatTime(iso) {
  const d = new Date(iso.endsWith('Z') || iso.includes('+') ? iso : iso + 'Z');
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) {
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  }
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export default function DMChat({
  otherUserId, me, onBlockChange, height = 240,
  title, emptyText, allowBlock = true
}) {
  const [messages, setMessages] = useState([]);
  const [other, setOther] = useState(null);
  const [iBlockedThem, setIBlockedThem] = useState(false);
  const [theyBlockedMe, setTheyBlockedMe] = useState(false);
  const [accessDenied, setAccessDenied] = useState(null);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState(null);
  const scrollerRef = useRef(null);
  const lastSeenId = useRef(0);
  const autoScroll = useRef(true);

  const loadMessages = useCallback(async () => {
    if (!otherUserId) return;
    try {
      const since = lastSeenId.current || 0;
      const res = await API.get(`/dm/with/${otherUserId}`, { params: { since } });
      setOther(res.data.other);
      setIBlockedThem(!!res.data.iBlockedThem);
      setTheyBlockedMe(!!res.data.theyBlockedMe);
      setAccessDenied(null);
      const incoming = res.data.messages || [];
      if (incoming.length === 0) return;
      setMessages(prev => {
        if (since === 0) return incoming;
        const ids = new Set(prev.map(m => m.id));
        return [...prev, ...incoming.filter(m => !ids.has(m.id))];
      });
      lastSeenId.current = Math.max(lastSeenId.current, ...incoming.map(m => m.id));
    } catch (e) {
      if (e.response?.status === 403) {
        setAccessDenied(e.response?.data?.error || 'Not allowed');
      } else if (e.response?.status === 404 || !e.response) {
        // Route not found (old backend still running) or network error
        setAccessDenied('Messaging is unavailable. The backend server may need to be restarted to pick up the new DM feature.');
      }
    }
  }, [otherUserId]);

  useEffect(() => {
    setMessages([]);
    lastSeenId.current = 0;
    loadMessages();
  }, [otherUserId, loadMessages]);

  useEffect(() => {
    const iv = setInterval(loadMessages, 3000);
    return () => clearInterval(iv);
  }, [loadMessages]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    if (autoScroll.current) el.scrollTop = el.scrollHeight;
  }, [messages]);

  const onScroll = () => {
    const el = scrollerRef.current;
    if (!el) return;
    autoScroll.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
  };

  const send = async () => {
    const body = text.trim();
    if (!body) return;
    setSending(true);
    setErr(null);
    try {
      const res = await API.post(`/dm/with/${otherUserId}`, { message: body });
      setText('');
      if (res.data?.message) {
        setMessages(prev => {
          if (prev.some(m => m.id === res.data.message.id)) return prev;
          return [...prev, res.data.message];
        });
        lastSeenId.current = Math.max(lastSeenId.current, res.data.message.id);
      }
      autoScroll.current = true;
    } catch (e) {
      setErr(e.response?.data?.error || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  const del = async (messageId) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await API.delete(`/dm/messages/${messageId}`);
      setMessages(prev => prev.filter(m => m.id !== messageId));
    } catch (e) {
      alert(e.response?.data?.error || 'Failed');
    }
  };

  const toggleBlock = async () => {
    try {
      if (iBlockedThem) {
        await API.delete(`/dm/block/${otherUserId}`);
        setIBlockedThem(false);
        onBlockChange?.(false);
      } else {
        if (!window.confirm(`Block ${other?.username || 'this user'}? You won't receive or send messages to each other.`)) return;
        await API.post(`/dm/block/${otherUserId}`);
        setIBlockedThem(true);
        onBlockChange?.(true);
      }
    } catch (e) {
      alert(e.response?.data?.error || 'Failed');
    }
  };

  if (accessDenied) {
    return <div style={s.wrap}><div style={s.noticeErr}>{accessDenied}</div></div>;
  }

  const composeDisabled = iBlockedThem || theyBlockedMe;
  let composeNotice = null;
  if (iBlockedThem) composeNotice = `You blocked ${other?.username || 'this user'}. Unblock to resume messaging.`;
  else if (theyBlockedMe) composeNotice = `You can't message ${other?.username || 'this user'} right now.`;

  return (
    <div style={s.wrap}>
      <div style={s.header}>
        <div style={s.title}>
          {title || `💬 DIRECT MESSAGE${other ? `: ${other.username.toUpperCase()}` : ''}`}
        </div>
        {allowBlock && (
          <button
            style={{ ...s.blockBtn, ...(iBlockedThem ? s.blockBtnActive : {}) }}
            onClick={toggleBlock}
            title={iBlockedThem ? 'Unblock user' : 'Block user'}
          >
            {iBlockedThem ? '🚫 Unblock' : 'Block'}
          </button>
        )}
      </div>

      <div
        style={{ ...s.scroller, height, maxHeight: height, minHeight: 120 }}
        ref={scrollerRef}
        onScroll={onScroll}
      >
        {messages.length === 0 ? (
          <div style={s.empty}>{emptyText || 'No messages yet. Start the trash talk 🔥'}</div>
        ) : (
          messages.map(m => {
            const mine = m.sender_id === me.id;
            const canDelete = mine || me.is_admin;
            return (
              <div key={m.id} style={s.msgRow}>
                <span style={{ ...s.author, ...(mine ? s.authorMine : {}) }}>
                  {m.sender_username}:
                </span>
                <span style={s.body}>{m.message}</span>
                <span style={s.time}>{formatTime(m.created_at)}</span>
                {canDelete && (
                  <button style={s.delBtn} onClick={() => del(m.id)} title="Delete message">✕</button>
                )}
              </div>
            );
          })
        )}
      </div>

      {composeNotice && <div style={s.notice}>{composeNotice}</div>}

      <div style={s.inputRow}>
        <input
          style={{ ...s.input, opacity: composeDisabled ? 0.5 : 1 }}
          placeholder={composeDisabled ? 'Messaging disabled' : 'Send a message...'}
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && !e.shiftKey && !composeDisabled && send()}
          maxLength={500}
          disabled={sending || composeDisabled}
        />
        <button
          style={{ ...s.sendBtn, opacity: sending || !text.trim() || composeDisabled ? 0.5 : 1 }}
          onClick={send}
          disabled={sending || !text.trim() || composeDisabled}
        >
          Send
        </button>
      </div>
      {err && <div style={{ fontSize: 12, color: '#ff8888' }}>⚠️ {err}</div>}
      {text.length > 400 && (
        <div style={{ fontSize: 11, color: '#556', textAlign: 'right' }}>{text.length}/500</div>
      )}
    </div>
  );
}
